import { useCallback, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'

import { api, ApiError, setToken } from './lib/api'
import { initLiff, type LiffState } from './lib/liff'
import Home from './screens/Home'
import Scan from './screens/Scan'
import Confirm from './screens/Confirm'
import Charging from './screens/Charging'
import History from './screens/History'
import { Screen, Notice } from './components/Layout'

export default function App() {
  const [liffState, setLiffState] = useState<LiffState | null>(null)
  const [displayName, setDisplayName] = useState<string | null>(null)
  const [fatal, setFatal] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    ;(async () => {
      const state = await initLiff()
      setLiffState(state)
      if (!state.ready || !state.idToken) return
      try {
        setToken(state.idToken)
        const me = await api.login(state.idToken)
        setDisplayName(me.display_name ?? state.displayName)
      } catch (e) {
        setFatal(e instanceof ApiError ? e.message : '無法登入，請稍後再試。')
      }
    })()
  }, [])

  /**
   * §12.2 — a customer with a live session almost certainly opened the app to check
   * on it or stop it, so route straight there rather than making them find it.
   */
  const resumeActive = useCallback(async () => {
    try {
      const active = await api.activeSession()
      if (active) navigate(`/charging/${active.session_id}`, { replace: true })
    } catch {
      // Non-fatal: home still works if this check fails.
    }
  }, [navigate])

  useEffect(() => {
    if (displayName !== null) void resumeActive()
  }, [displayName, resumeActive])

  if (fatal) {
    return (
      <Screen title="EV 充電服務">
        <Notice tone="error">{fatal}</Notice>
      </Screen>
    )
  }

  if (!liffState?.ready) {
    return (
      <Screen title="EV 充電服務">
        <Notice tone="muted">載入中…</Notice>
      </Screen>
    )
  }

  if (liffState.error) {
    return (
      <Screen title="EV 充電服務">
        <Notice tone="error">{liffState.error}</Notice>
      </Screen>
    )
  }

  return (
    <Routes>
      <Route path="/" element={<Home displayName={displayName} />} />
      <Route
        path="/charging/start"
        element={<Scan scannerAvailable={liffState.scannerAvailable} />}
      />
      <Route path="/charging/confirm" element={<Confirm />} />
      <Route path="/charging/:sessionId" element={<Charging />} />
      <Route path="/history" element={<History />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
